import { Card, FlexBox, Li, Ul, H4, Box } from '@vp/swan';
import { useFeature } from '@vp/core-ui-components';
import type { OrderSummaryLineItem } from '../../../types/OrderHistory.types';
import { useOrderSummary } from '../../contexts/OrderSummaryContext';
import { DesignFeedbackBanner } from '../designFeedback/DesignFeedbackBanner';
import { useCustomerFeedbackGroupForItemGroup } from '../../contexts/CustomerFeedbackGroupsContext';
import { HistoryLineItem } from '../HistoryLineItem';

interface LineItemGroupProps {
  items: OrderSummaryLineItem[];
  groupId: string;
}

export const LineItemGroup = ({ items, groupId }: LineItemGroupProps) => {
  const {
    order: { itemGroups },
  } = useOrderSummary();

  const groupData = itemGroups.find((group) => group.groupId === groupId);

  // Get the feedback group associated with one of the items in the item group.
  // It is assumed that if a feedback group exists, this feedback group is associated with all the items in the item group.
  const {
    customerFeedbackGroup: pendingDesignFeedbackGroup,
    customerFeedbackGroupExists: pendingDesignFeedbackGroupExists,
  } = useCustomerFeedbackGroupForItemGroup({
    lineItemIds: groupData?.lineItemIds || [],
    requestType: 'DOCUMENT_REVIEW',
    groupState: 'AWAITING_FEEDBACK',
  });

  const isFulfillmentHistoryEnabled = useFeature('enableFulfillmentHistory');

  // Display group level DesignFeedbackBanner based on the first item in the feedback group
  const firstLineItemId =
    pendingDesignFeedbackGroupExists &&
    pendingDesignFeedbackGroup.lineItemIds[0];

  const itemGroupTitle = groupData?.groupDisplayName;

  return (
    <Box aria-labelledby={`group-title-${groupId}`} mb={7}>
      <Card bordered>
        {itemGroupTitle && (
          <FlexBox justifyContent="flex-start" flexWrap="wrap" pr={5} mb={3}>
            <H4
              fontSize="standard"
              fontWeight="bold"
              data-testid={`group-title-${groupId}-h4`}
              id={`group-title-${groupId}`}
            >
              {itemGroupTitle}
            </H4>
          </FlexBox>
        )}
        {firstLineItemId && !isFulfillmentHistoryEnabled && (
          <FlexBox>
            <DesignFeedbackBanner lineItemId={firstLineItemId} />
          </FlexBox>
        )}
        <Ul skin="minimal">
          {items.map((item) => {
            return (
              <Li key={`item-${item.id}`} data-testid={`item-${item.id}`}>
                <HistoryLineItem item={item} />
              </Li>
            );
          })}
        </Ul>
      </Card>
    </Box>
  );
};
